import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight, Play, Sparkles, BadgeCheck } from 'lucide-react';

export default function Hero() {
  const [score, setScore] = useState(300);

  useEffect(() => {
    let raf;
    const start = performance.now();
    const animate = (t) => {
      const p = Math.min(1, (t - start) / 1600);
      const eased = 1 - Math.pow(1 - p, 3);
      setScore(Math.round(300 + eased * 465));
      if (p < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, []);

  const scorePct = ((score - 300) / (850 - 300)) * 100;
  const scoreLabel = score >= 800 ? 'Excellent' : score >= 740 ? 'Very Good' : score >= 670 ? 'Good' : score >= 580 ? 'Fair' : 'Poor';

  return (
    <section className="relative overflow-hidden bg-hero-glow">
      <div className="absolute inset-0 grid-pattern opacity-60 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="fadeInUp">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-emerald-200 shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Compare. Choose. Save.</span>
            </div>
            <h1 className="font-serif-display mt-6 text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-slate-900 font-bold">
              Every <span className="text-emerald-600">Financial Product</span><br className="hidden sm:inline" /> Compared In One Place
            </h1>
            <p className="mt-5 sm:mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              The Loan Alliance is an independent, editor-led comparison platform for personal loans, credit protection, credit builders, and wealth-building products. See real rates, features, and reviews side-by-side — pick with confidence in minutes.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/loan">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6 text-base shine group">
                  Compare Loans
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/how-it-works">
                <Button variant="outline" className="h-12 px-6 text-base border-slate-300 hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-700">
                  <Play className="mr-2 h-4 w-4" />
                  How It Works
                </Button>
              </Link>
            </div>

          </div>

          {/* Credit score card */}
          <div className="relative fadeInUp" style={{ animationDelay: '0.15s' }}>
            <div className="absolute -top-6 -right-6 w-72 h-72 bg-emerald-200/40 rounded-full blur-3xl" />
            <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-amber-200/40 rounded-full blur-3xl" />

            <div className="relative rounded-2xl bg-white shadow-2xl shadow-emerald-900/10 border border-slate-100 p-6 lg:p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Your Credit Score</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{scoreLabel}</p>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs font-semibold">
                  <BadgeCheck className="w-3.5 h-3.5" /> Verified
                </div>
              </div>

              <div className="relative">
                <svg viewBox="0 0 200 110" className="w-full">
                  <defs>
                    <linearGradient id="gauge" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#ef4444" />
                      <stop offset="30%" stopColor="#f59e0b" />
                      <stop offset="60%" stopColor="#eab308" />
                      <stop offset="85%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#059669" />
                    </linearGradient>
                  </defs>
                  <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke="#e2e8f0" strokeWidth="14" strokeLinecap="round" />
                  <path d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke="url(#gauge)" strokeWidth="14" strokeLinecap="round"
                    strokeDasharray={`${(scorePct / 100) * 251} 251`} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
                  <div className="text-4xl font-bold text-slate-900 tabular-nums">{score}</div>
                  <div className="text-xs text-slate-500">out of 850</div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                {['Poor', 'Fair', 'Good'].map((l, i) => (
                  <div key={l} className={`rounded-lg py-1.5 px-2 text-xs font-medium ${i === 2 && scoreLabel === 'Good' ? 'bg-amber-100 text-amber-800' : 'bg-slate-50 text-slate-500'}`}>{l}</div>
                ))}
              </div>
              <div className="mt-2 grid grid-cols-2 gap-3 text-center">
                {['Very Good', 'Excellent'].map((l) => (
                  <div key={l} className={`rounded-lg py-1.5 px-2 text-xs font-medium ${l === scoreLabel ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-50 text-slate-500'}`}>{l}</div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-slate-100 space-y-3">
                <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">Credit Score Factors</p>
                {[
                  { name: 'Payment History', level: 'Excellent', w: 95 },
                  { name: 'Credit Utilization', level: 'Very Good', w: 82 },
                  { name: 'Credit Age', level: 'Excellent', w: 90 }
                ].map((f) => (
                  <div key={f.name}>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700 font-medium">{f.name}</span>
                      <span className="text-emerald-700 font-semibold">{f.level}</span>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-1000" style={{ width: `${f.w}%` }} />
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/loan">
                <Button className="w-full mt-6 h-11 bg-slate-900 hover:bg-slate-800 text-white">Compare Rates</Button>
              </Link>
            </div>

            <div className="absolute -right-4 top-8 rotate-6 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-700 text-white p-4 shadow-lg hidden md:block">
              <div className="flex items-center gap-2">
                <BadgeCheck className="w-4 h-4" />
                <span className="text-xs uppercase font-semibold tracking-wider">Approved</span>
              </div>
              <div className="mt-1 text-2xl font-bold">$25K</div>
              <div className="text-xs opacity-80">Loan Offer</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
